import { IS_SSR } from '@libs/common-next';

const SKU_SEARCH_TERM_KEY = 'skuSearchTerms';

/**
 * Store the search term for a specific SKU in sessionStorage.
 * sessionStorage is used intentionally so the search term is cleared when the browser session ends.
 * Pass null when the item was not found via search (e.g. browsed via nav or banner).
 */
export const setSkuSearchTerm = (sku: string, searchTerm: string | null): void => {
    if (!sku || IS_SSR) return;

    try {
        const existing = sessionStorage.getItem(SKU_SEARCH_TERM_KEY);
        const map: Record<string, string | null> = existing ? JSON.parse(existing) : {};

        map[sku] = searchTerm;

        sessionStorage.setItem(SKU_SEARCH_TERM_KEY, JSON.stringify(map));
    } catch (error) {
        console.error('Error storing SKU search term:', error);
    }
};

/**
 * Retrieve the search term for a specific SKU from sessionStorage.
 *
 * - `undefined` — nothing stored for this SKU (or unreadable / SSR / missing sku): callers may fall back (e.g. live search stats).
 * - `null` — explicitly stored as non-search (`setSkuSearchTerm(sku, null)`): callers must not substitute search stats.
 * - `string` — search term for this SKU.
 */
export const getSkuSearchTerm = (sku: string | undefined): string | null | undefined => {
    if (!sku || IS_SSR) return undefined;

    try {
        const existing = sessionStorage.getItem(SKU_SEARCH_TERM_KEY);
        if (!existing) return undefined;

        const map: Record<string, string | null> = JSON.parse(existing);
        if (!Object.prototype.hasOwnProperty.call(map, sku)) return undefined;
        return map[sku];
    } catch (error) {
        return undefined;
    }
};

/**
 * GA4 `item_search_term`: use session value when present (including explicit `null`);
 * only when the SKU has no session entry, use the computed fallback.
 */
export const resolveItemSearchTerm = (
    storedSkuTerm: string | null | undefined,
    fallbackTerm: string | null
): string | null => (storedSkuTerm !== undefined ? storedSkuTerm : fallbackTerm);

/**
 * Clear all SKU search term mappings from sessionStorage.
 * Should be called after purchase completion.
 */
export const clearSkuSearchTerms = (): void => {
    if (IS_SSR) return;

    sessionStorage.removeItem(SKU_SEARCH_TERM_KEY);
};
